import { useState } from 'react';
import { Badge, Group, Loader, PasswordInput, Text } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import type { CSSProperties, ReactNode } from 'react';

/**
 * A password/secret input that surfaces two things the plain `PasswordInput`
 * can't, uniformly across every secret field in the app:
 *
 *   1. **Present indicator** — a green ✓ "Saved" badge when a value is already
 *      stored server-side, so the operator knows a key/token exists without
 *      seeing it.
 *   2. **Reveal-on-demand** — the eye toggle can pull the *saved* plaintext
 *      (not just un-hide what was typed). Because saved secrets are never sent
 *      to the browser on page load, the value is fetched lazily via `onReveal`
 *      only when the eye is opened. This mirrors the existing
 *      `webhooks.getAll({ showTokens })` reveal pattern.
 *
 * Editing is unchanged: typing a new value overwrites, and leaving the field on
 * its stored mask (`maskSentinel`) means "keep the existing secret" — the
 * server's `set()/setMany()` already drop the mask sentinel on save, so this
 * component never has to push the revealed value back into the form draft.
 */
export interface SecretFieldProps {
  label?: ReactNode;
  description?: ReactNode;
  placeholder?: string;
  /** Current draft value the parent form owns. */
  value: string;
  onChange: (value: string) => void;
  /** True when a secret is already saved server-side (drives the ✓ badge and
   *  whether the eye needs to fetch). */
  present: boolean;
  /** Lazily fetch the saved plaintext when the eye is opened. Omit for fields
   *  whose value is never revealable. */
  onReveal?: () => Promise<string | null | undefined>;
  /** The value that means "unchanged" for a masked secret (default the config
   *  service mask). A draft equal to this is treated as "no typed value". */
  maskSentinel?: string;
  error?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  style?: CSSProperties;
  /** Forwarded to the underlying input (e.g. react-hook-form field ref). */
  name?: string;
  onBlur?: () => void;
}

const DEFAULT_MASK = '••••••••';

export function SecretField({
  label,
  description,
  placeholder,
  value,
  onChange,
  present,
  onReveal,
  maskSentinel = DEFAULT_MASK,
  error,
  disabled,
  required,
  style,
  name,
  onBlur,
}: SecretFieldProps) {
  const [visible, setVisible] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // The draft only holds a real, editable value once the operator types one.
  // Until then a masked secret loads as `maskSentinel` (or empty), which must
  // NOT be shown as if the operator typed it.
  const hasTypedValue = value !== '' && value !== maskSentinel;
  const displayValue = revealed !== null ? revealed : hasTypedValue ? value : '';

  const handleVisibilityChange = async (next: boolean) => {
    if (next && !hasTypedValue && revealed === null && present && onReveal) {
      setLoading(true);
      try {
        const v = await onReveal();
        setRevealed(v ?? '');
      } catch {
        setRevealed('');
      } finally {
        setLoading(false);
      }
    }
    setVisible(next);
  };

  const labelNode = (
    <Group gap={6} wrap="nowrap" component="span">
      <span>{label}</span>
      {present && (
        <Badge
          size="xs"
          color="green"
          variant="light"
          leftSection={<IconCheck size={10} />}
          styles={{ root: { textTransform: 'none' } }}
        >
          Saved
        </Badge>
      )}
      {loading && <Loader size={12} />}
    </Group>
  );

  return (
    <PasswordInput
      label={label ? labelNode : undefined}
      description={description}
      placeholder={present && !hasTypedValue ? maskSentinel : placeholder}
      value={displayValue}
      onChange={(e) => {
        setRevealed(null);
        onChange(e.currentTarget.value);
      }}
      visible={visible}
      onVisibilityChange={(next) => void handleVisibilityChange(next)}
      error={error}
      disabled={disabled}
      required={required}
      name={name}
      onBlur={onBlur}
      style={style}
    />
  );
}

/** Standalone "Saved / Not set" indicator for secrets rendered outside a
 *  SecretField (e.g. read-only status rows). */
export function SecretPresentBadge({ present }: { present: boolean }) {
  return present ? (
    <Badge size="xs" color="green" variant="light" leftSection={<IconCheck size={10} />}>
      Saved
    </Badge>
  ) : (
    <Text span size="xs" c="dimmed">
      Not set
    </Text>
  );
}
